<template>
    <v-row v-if="!opening" no-gutters class="px-6">
        <SupplierTypeEditDialog
            v-if="dialogShow"
            :account-id="selectedAccount.id"
            :supplier-type-id="dialogId"
            :show-dialog="dialogShow"
            :clone-type-id="dialogCloneId"
            @close_modal="closeModal"
        ></SupplierTypeEditDialog>

        <v-col cols="12">
            <v-row no-gutters>
                <AppStoreListHeader
                    :item-label="$t('Supplier types')"
                    :add-label="$t('Add')"
                    :show-button="appStoreDialogFlags.canManageAccount()"
                    @add-item="addSupplierType"
                />
            </v-row>
            <v-row no-gutters>
                <v-col v-if="types.length === 0" class="px-3 pt-4 body-2 grey--text text--darken-2">
                    {{ $t("No supplier types yet") }}
                </v-col>
                <v-col v-else>
                    <v-data-table
                        :headers="headers"
                        :items="types"
                        disable-pagination
                        :footer-props="{ disablePagination : true, disableItemsPerPage : true }"
                        hide-default-footer
                        hide-default-header
                        class="table-bottom-line table-top-line"
                    >
                        <template v-slot:body="{ items }">
                            <draggable
                                :list="types"
                                tag="tbody"
                                :options="{disabled : !draggableEnabled}"
                                @update="onOrderChanged"
                            >
                                <tr v-for="(item, idx) in types" :key="item.id">
                                    <td style="width: 30px; padding-top: 7px; padding-right: 4px;" class="drag-icon">
                                        <v-img
                                            v-if="draggableEnabled"
                                            :max-width="10"
                                            class="show-hover"
                                            :src="require('../../../assets/drag-icon.svg')"
                                        />
                                    </td>
                                    <td>{{ item.title }}</td>
                                    <td style="text-align: right;">
                                        <action-menu
                                            v-if="rowActions.length > 0"
                                            :items="rowActions"
                                            @action="rowActionClick(item, rowActions[$event].action)"
                                        />
                                    </td>
                                </tr>
                            </draggable>
                        </template>
                    </v-data-table>
                </v-col>
            </v-row>
        </v-col>
    </v-row>
</template>

<script>
import { ref, computed } from "@vue/composition-api";
import { $t } from "@/i18n";
import AppModule from "../../../store/modules/app";
import AppStoreListHeader from "../../../components/AppStore/AppStoreListHeader";
import AppStoreDialog from "@/store/modules/AppStoreDialog/appStoreDialog";
import SupplierTypeEditDialog from "../Supplier/SupplierTypeEditDialog";
import DialogModule from "../../../store/modules/dialog";
import SupplierModule from "../../../store/modules/supplier";
import draggable from "vuedraggable";
import errorDialog from "@/views/ErrorDialog/errorDialog"

export default {
    name: "SupplierDialogTypesTab",
    components: {
        SupplierTypeEditDialog, draggable, AppStoreListHeader
    },
    setup() {
        const { fetchClassifiers, supplier_types, removeFromClassifiersCache, syncToClassifiersCache, rights_masks } = AppModule();
        const { appStoreDialogGetters, appStoreDialogFlags } = AppStoreDialog();
        const { deleteSupplierType, saveSupplierTypeOrder } = SupplierModule();
        const dialogModule = DialogModule();

        const selectedAccount = appStoreDialogGetters.getSelectedCompany();

        const headers = [
            { value: "title", text: $t("Name"), sortable: false },
            { value: "edit", text: "", align: "right", sortable: false }
        ];
        const opening = ref(true);
        const dialogId = ref(0);
        const dialogShow = ref(false);
        const deleteId = ref(0);
        const dialogCloneId = ref(0);
        const types = ref([]);

        const draggableEnabled = computed(() => {
            return (selectedAccount.rights & rights_masks.value.manage_account) === rights_masks.value.manage_account;
        });

        const loadData = () => {
            let data = { types: ["custom_fields", "supplier_types", "default_custom_fields"], account_id: selectedAccount.id };
            fetchClassifiers(data)
                .then(() => {
                    opening.value = false;
                    types.value = supplier_types.value[selectedAccount.id];
                })
                .catch(ex => errorDialog.showError(ex));
        };
        const rowActions = computed(() => {
            if (!appStoreDialogFlags.canManageAccount()) return [];
            return [
                { action: "edit", text: $t("Edit"), icon: "Pencil" },
                { action: "clone", text: $t("Duplicate"), icon: "Copy" },
                { action: "delete", text: $t("Delete"), icon: "Trash" }
            ]
        });
        const rowActionClick = (item, action) => {
            switch (action) {
                case "edit":
                    dialogId.value = item.id;
                    dialogShow.value = true;
                    break;
                case "clone":
                    dialogId.value = 0;
                    dialogCloneId.value = item.id;
                    dialogShow.value = true;
                    break;
                case "delete":
                    deleteClick(item.id);
                    break;
            }
        }
        const deleteClick = id => {
            deleteId.value = id;
            dialogModule.setConfirmOptions({
                title: $t("Are you sure you wish to delete this supplier type?"),
                confirm_text: $t("Delete"),
                cancel_text: $t("Cancel"),
                action: deleteConfirmed
            });
            dialogModule.openConfirmDialog();
        };
        const deleteConfirmed = () => {
            deleteSupplierType(selectedAccount.id, deleteId.value)
                .then(() => {
                    removeFromClassifiersCache("supplier_types", deleteId.value, selectedAccount.id);
                })
                .catch(ex => errorDialog.showError(ex));
        };
        const addSupplierType = () => {
            dialogId.value = 0;
            dialogCloneId.value = 0;
            dialogShow.value = true;
        };
        const closeModal = () => {
            dialogShow.value = false;
            dialogCloneId.value = 0;
            dialogId.value = 0;
        };
        const onOrderChanged = (a, b, c) => {
            let data = { supplier_types: types.value.map(x => x.id), account_id: selectedAccount.id };
            saveSupplierTypeOrder(data)
                .then(() => {
                    for (let i = 0; i < types.value.length; i++) {
                        types.value[i].sort_order = (i + 1) * 100;
                        syncToClassifiersCache("supplier_types", types.value[i], selectedAccount.id);
                    }
                })
                .catch(ex => errorDialog.showError(ex));
        }

        loadData();

        return {
            headers,
            opening,
            types,
            selectedAccount,
            dialogId,
            dialogShow,
            dialogCloneId,
            appStoreDialogFlags,
            rowActions,
            draggableEnabled,
            rowActionClick,
            closeModal,
            addSupplierType,
            deleteClick,
            onOrderChanged
        };
    }
};
</script>
